import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTour } from '@reactour/tour';

export const CustomMask: React.FC = () => {
  const { isOpen, currentStep, steps } = useTour();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const observedElement = useRef<Element | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const animationFrameRef = useRef<number>();

  // Mémoriser la fonction pour éviter les boucles infinies
  const updateRect = useCallback(() => {
    if (observedElement.current) {
      const newRect = observedElement.current.getBoundingClientRect();
      if (
        rect?.top !== newRect.top ||
        rect?.left !== newRect.left ||
        rect?.width !== newRect.width ||
        rect?.height !== newRect.height
      ) {
        setRect(newRect);
      }
    }
  }, [rect?.top, rect?.left, rect?.width, rect?.height]);

  const observePosition = useCallback(() => {
    if (observedElement.current) {
      updateRect();
      animationFrameRef.current = requestAnimationFrame(observePosition);
    }
  }, [updateRect]);

  useEffect(() => {
    if (!isOpen || !steps || !steps[currentStep]) {
      setRect(null);
      observedElement.current = null;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }
    const selector = steps[currentStep].selector;
    if (!selector || selector === 'none') {
      setRect(null);
      observedElement.current = null;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      return;
    }
    const el =
      typeof selector === 'string'
        ? document.querySelector(selector)
        : selector;
    if (el && el instanceof Element) {
      observedElement.current = el;
      setRect(el.getBoundingClientRect());

      // Observer la taille de l'élément
      if (resizeObserver.current) resizeObserver.current.disconnect();
      resizeObserver.current = new ResizeObserver(updateRect);
      resizeObserver.current.observe(el);

      // Démarrer l'observation de la position
      animationFrameRef.current = requestAnimationFrame(observePosition);

      // Écouter scroll et resize
      window.addEventListener('scroll', updateRect, true);
      window.addEventListener('resize', updateRect);
    } else {
      setRect(null);
      observedElement.current = null;
    }
    return () => {
      if (resizeObserver.current) resizeObserver.current.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [isOpen, currentStep, steps, updateRect, observePosition]);

  if (!isOpen || !rect) return null;

  return (
    <div
      className="border-2 border-dashed border-primary"
      style={{
        position: 'fixed',
        top: rect.top + window.scrollY - 10,
        left: rect.left + window.scrollX - 10,
        width: rect.width + 20,
        height: rect.height + 20,
        borderRadius: 0,
        pointerEvents: 'none',
        zIndex: 10002,
        boxSizing: 'border-box',
      }}
    />
  );
};
